import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

export const HEADER_LEGACY_FIELD_ORDER = [
  "display_logo",
  "logo",
  {
    type: "groupedFields",
    handle: "logo_options",
    fields: ["position", "objectFit", "maxWidth", "maxHeight"],
  },
  { type: "separator" },
  "display_back_button",
  "back_link_url",
  // Opens the admin-only block below, so it follows the same visibility.
  { type: "separator", allowedRoles: ["ADMIN"] },
  "position_absolute_on_mobile",
  "position_absolute_on_desktop",
  "display_hamburger",
  { type: "separator" },
  "hamburger_color",
  "background_color",
  "label_color",
  "icon_color",
  "icon_background_color",
  "button_text_color",
  "button_background_color",
  { type: "separator" },
  "shop_search_location_field_background_color",
  "shop_search_location_field_text_color",
  "shop_search_location_field_placeholder_color",
  "shop_search_location_field_icon_color",
  { type: "separator" },
  "left_blocks",
  "right_blocks",
  "navigation_links",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  headerBlockDescriptor: _generatedHeaderBlockDescriptor,
  headerBlockBlocDefinition,
  headerBlockDashboardLayout,
  headerBlockConfig,
  headerBlockResolveConfig,
  headerBlockProperties,
} = doodBlockConfig(
  {
    name: "Header",
    componentType: "HeaderBlockComponent",
    category: "navigation",
    description: "Marketplace header with logo, back button and navigation links.",
    keywords: ["header", "navigation", "logo", "menu"],
    sharedFields: {},
    slots: [
      {
        key: "left_blocks",
        name: "pages.blocks.header.left-blocks.label",
        accept: "all",
        slotItemFormat: "ids",
      },
      {
        key: "right_blocks",
        name: "pages.blocks.header.right-blocks.label",
        accept: "all",
        slotItemFormat: "ids",
      },
      {
        key: "navigation_links",
        name: "pages.blocks.header.navigation-links.label",
        accept: "all",
        slotItemFormat: "ids",
      },
    ],
  },

  () => ({
    display_logo: propertyValue({
      type: "checkbox",
      value: false,
      options: { label: "pages.blocks.header.display-logo.label" },
    }),
    logo: propertyValue({
      type: "image",
      value: null,
      options: {
        label: "pages.blocks.header.logo.label",
        help: "pages.blocks.header.logo.help",
      },
    }),
    logo_options: {
      position: propertyValue({
        type: "select",
        value: "left",
        options: {
          label: "pages.blocks.header.logo-options.position.label",
          help: "pages.blocks.header.logo-options.position.help",
          choices: [
            { value: "left", label: "pages.blocks.header.logo-options.position.options.left" },
            {
              value: "center",
              label: "pages.blocks.header.logo-options.position.options.center",
            },
          ],
        },
      }),
      objectFit: propertyValue({
        type: "select",
        value: "fill",
        options: {
          label: "pages.blocks.header.logo-options.object-fit.label",
          choices: [
            {
              value: "contain",
              label: "pages.blocks.header.logo-options.object-fit.options.contain",
            },
            { value: "cover", label: "pages.blocks.header.logo-options.object-fit.options.cover" },
            { value: "fill", label: "pages.blocks.header.logo-options.object-fit.options.fill" },
            { value: "none", label: "pages.blocks.header.logo-options.object-fit.options.none" },
            {
              value: "scale-down",
              label: "pages.blocks.header.logo-options.object-fit.options.scale-down",
            },
          ],
        },
      }),
      maxWidth: propertyValue({
        type: "text",
        value: "",
        options: {
          label: "pages.blocks.header.logo-options.max-width.label",
          help: "pages.blocks.header.logo-options.max-width.help",
        },
      }),
      maxHeight: propertyValue({
        type: "text",
        value: "",
        options: {
          label: "pages.blocks.header.logo-options.max-height.label",
          help: "pages.blocks.header.logo-options.max-height.help",
        },
      }),
    },
    display_back_button: propertyValue({
      type: "checkbox",
      value: true,
      options: { label: "pages.blocks.header.display-back-button.label" },
    }),
    back_link_url: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.header.back-link-url.label",
        help: "pages.blocks.header.back-link-url.help",
      },
    }),
    position_absolute_on_mobile: propertyValue({
      type: "checkbox",
      value: false,
      allowedRoles: ["ADMIN"],
      options: {
        label: "pages.blocks.header.position-absolute-on-mobile.label",
        help: "pages.blocks.header.position-absolute-on-mobile.help",
      },
    }),
    position_absolute_on_desktop: propertyValue({
      type: "checkbox",
      value: false,
      allowedRoles: ["ADMIN"],
      options: {
        label: "pages.blocks.header.position-absolute-on-desktop.label",
        help: "pages.blocks.header.position-absolute-on-desktop.help",
      },
    }),
    display_hamburger: propertyValue({
      type: "checkbox",
      value: true,
      allowedRoles: ["ADMIN"],
      options: { label: "pages.blocks.header.display-hamburger.label" },
    }),
    hamburger_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: { label: "pages.blocks.header.hamburger-color.label" },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: { label: "pages.blocks.header.background-color.label" },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: { label: "pages.blocks.header.text-color.label" },
    }),
    label_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.header.label-color.label",
        help: "pages.blocks.header.label-color.help",
      },
    }),
    icon_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-600" },
      options: {
        label: "pages.blocks.header.icon-color.label",
        help: "pages.blocks.header.icon-color.help",
      },
    }),
    icon_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-100" },
      options: { label: "pages.blocks.header.icon-background-color.label" },
    }),
    button_text_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: { label: "pages.blocks.header.button-text-color.label" },
    }),
    button_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "primary-600" },
      options: {
        label: "pages.blocks.header.button-background-color.label",
        help: "pages.blocks.header.button-background-color.help",
      },
    }),
    shop_search_location_field_background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: {
        label:
          "pages.blocks.header.shop-search-location-field-background-color.label",
      },
    }),
    shop_search_location_field_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label: "pages.blocks.header.shop-search-location-field-text-color.label",
      },
    }),
    shop_search_location_field_placeholder_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-500" },
      options: {
        label:
          "pages.blocks.header.shop-search-location-field-placeholder-color.label",
        help: "pages.blocks.header.shop-search-location-field-placeholder-color.help",
      },
    }),
    shop_search_location_field_icon_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.header.shop-search-location-field-icon-color.label",
      },
    }),
  }),

  () => ({
    omitFields: ["text_color"] as const,
    tabs: [
      {
        label: "pages.blocks.header.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.header.fieldset.appearance.label",
            fields: [
              "background_color",
              "position_absolute_on_mobile",
              "position_absolute_on_desktop",
            ],
          },
          {
            legend: "pages.blocks.header.fieldset.logo.label",
            fields: [
              "display_logo",
              { prop: "logo", visibleWhen: { display_logo: true } },
              {
                prop: "logo_options.position",
                visibleWhen: { display_logo: true },
              },
              {
                prop: "logo_options.objectFit",
                visibleWhen: { display_logo: true },
              },
              {
                prop: "logo_options.maxWidth",
                visibleWhen: { display_logo: true },
              },
              {
                prop: "logo_options.maxHeight",
                visibleWhen: { display_logo: true },
              },
            ],
          },
          {
            legend: "pages.blocks.header.fieldset.back-button.label",
            fields: [
              "display_back_button",
              {
                prop: "back_link_url",
                visibleWhen: { display_back_button: true },
              },
            ],
          },
          {
            legend: "pages.blocks.header.fieldset.menu.label",
            fields: [
              "display_hamburger",
              {
                prop: "hamburger_color",
                visibleWhen: { display_hamburger: true },
              },
            ],
          },
          {
            legend: "pages.blocks.header.fieldset.icons-and-texts.label",
            fields: ["icon_color", "icon_background_color", "label_color"],
          },
          {
            legend: "pages.blocks.header.fieldset.cart-button.label",
            fields: ["button_background_color", "button_text_color"],
          },
          {
            legend: "pages.blocks.header.fieldset.address-field.label",
            fields: [
              "shop_search_location_field_background_color",
              "shop_search_location_field_text_color",
              "shop_search_location_field_placeholder_color",
              "shop_search_location_field_icon_color",
            ],
          },
          {
            legend: "pages.blocks.header.fieldset.blocks.label",
            fields: ["left_blocks", "right_blocks", "navigation_links"],
          },
        ],
      },
    ],
  }),
);

export function headerBlockDescriptor(): IBlockDescriptor;
export function headerBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function headerBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedHeaderBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedHeaderBlockDescriptor,
    headerBlockProperties,
    fieldOrder,
  );
}

export {
  headerBlockBlocDefinition,
  headerBlockDashboardLayout,
  headerBlockConfig,
  headerBlockResolveConfig,
  headerBlockProperties,
};

export type HeaderBlockConfig = typeof headerBlockConfig;
